import { z } from 'zod';

// renderChart tool input; caps (6 series / 100 rows) keep a malformed model call from blowing up the render.
// Rows are keyed on 'name' for the category axis with numeric measures alongside: recharts silently renders
// a blank plot for a non-numeric or absent dataKey, so the shape is pinned here rather than left to the model.
const BaseChartArgs = z.object({
  title: z.string().max(120).optional(),
  series: z
    .array(z.object({ key: z.string().min(1), label: z.string().optional() }))
    .min(1)
    .max(6),
});

export const CartesianChartArgs = BaseChartArgs.safeExtend({
  chartType: z.enum(['bar', 'line', 'area']),
  data: z
    .array(z.object({ name: z.string() }).catchall(z.number()))
    .min(1)
    .max(100),
}).check((ctx) => {
  // catchall types the cells but can't tie them to the series keys; an absent key plots nothing.
  const { series, data } = ctx.value;
  for (const [i, s] of series.entries()) {
    if (data.some((row) => typeof row[s.key] !== 'number')) {
      ctx.issues.push({
        code: 'custom',
        input: data,
        path: ['series', i, 'key'],
        message: `Every data row needs a numeric "${s.key}".`,
      });
    }
  }
});
export type CartesianChartArgs = z.infer<typeof CartesianChartArgs>;

// Pie plots one measure over a whole, so its shape is fixed — which makes the check above unnecessary here.
export const PieChartArgs = BaseChartArgs.safeExtend({
  chartType: z.literal('pie'),
  series: z.array(z.object({ key: z.literal('value'), label: z.string().optional() })).length(1),
  data: z
    .array(z.object({ name: z.string(), value: z.number() }))
    .min(1)
    .max(100),
});
export type PieChartArgs = z.infer<typeof PieChartArgs>;

export const RenderChartArgs = z.discriminatedUnion('chartType', [
  CartesianChartArgs,
  PieChartArgs,
]);
export type RenderChartArgs = z.infer<typeof RenderChartArgs>;
